// ViewLogic Router - ES6 Module
import { I18nManager } from './plugins/I18nManager.js';
import { AuthManager } from './plugins/AuthManager.js';
import { CacheManager } from './plugins/CacheManager.js';
import { QueryManager } from './plugins/QueryManager.js';
import { RouteLoader } from './core/RouteLoader.js';
import { ErrorHandler } from './core/ErrorHandler.js';
import { StateHandler } from './core/StateHandler.js';

export class ViewLogicRouter {
    constructor(options = {}) {
        // 버전 정보
        this.version = options.version || '1.0.0';
        
        // 기본 환경설정 최적화
        this.config = this._buildConfig(options);
        
        this.currentHash = '';
        this.currentVueApp = null;
        this.previousVueApp = null; // 이전 Vue 앱 (전환 효과를 위해 보관)

        // LoadingManager가 없을 때를 위한 기본 전환 상태
        this.transitionInProgress = false;

        // Progress bar state
        this.progressBarTimer = null;
        this.progressBarElement = null;

        // 초기화 준비 상태
        this.isReady = false;
        this.readyPromise = null;
        
        // 이벤트 리스너 바인딩 최적화
        this._boundHandleRouteChange = this.handleRouteChange.bind(this);
        
        // 모든 초기화를 한번에 처리
        this.readyPromise = this.initialize();
    }

    /**
     * 설정 빌드 (분리하여 가독성 향상)
     */
    _buildConfig(options) {
        const currentOrigin = window.location.origin;
        
        const defaults = {
            basePath: '/',                  // 애플리케이션 기본 경로 (서브폴더 배포용)
            srcPath: '/src',               // 소스 파일 경로
            mode: 'hash',
            cacheMode: 'memory',
            cacheTTL: 300000,
            maxCacheSize: 50,
            useLayout: true,
            defaultLayout: 'default',
            defaultRoute: 'home',          // 기본 라우트 (/#/ 접근 시)
            environment: 'development',
            routesPath: '/routes',         // 프로덕션 라우트 경로
            enableErrorReporting: true,
            useI18n: false,
            defaultLanguage: 'ko',
            i18nPath: '/i18n',            // 다국어 파일 경로
            logLevel: 'info',
            apiBaseURL: '',
            requestTimeout: 30000,
            uploadTimeout: 300000,
            authEnabled: false,
            loginRoute: 'login',
            protectedRoutes: [],
            protectedPrefixes: [],
            publicRoutes: ['login', 'register', 'home'],
            checkAuthFunction: null,
            redirectAfterLogin: 'home',
            authCookieName: 'authToken',
            authStorage: 'localStorage',
            refreshToken: null,                // 토큰 갱신 콜백 (async () => { accessToken, refreshToken? })
            refreshTokenStorage: null,          // 리프레시 토큰 저장소 (null이면 authStorage 사용)
            apiInterceptors: null               // API 응답/에러 인터셉터 ({ response?, error? })
        };
        
        const config = { ...defaults, ...options };

        // 절대 경로들을 basePath 기준으로 해결
        config.srcPath = this.resolvePath(config.srcPath, config.basePath);
        config.routesPath = this.resolvePath(config.routesPath, config.basePath);
        config.i18nPath = this.resolvePath(config.i18nPath, config.basePath);

        return config;
    }

    /**
     * 두 경로를 안전하게 조합 및 정규화 (기본 유틸리티)
     * @param {string} basePath - 기본 경로
     * @param {string} relativePath - 상대 경로
     * @returns {string} 조합된 경로 (예: '/examples' + '/about' → '/examples/about')
     */
    combinePaths(basePath, relativePath) {
        if (!basePath || basePath === '/') {
            return relativePath.replace(/\/+/g, '/');
        }

        const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
        const cleanRelative = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
        const combined = `${cleanBase}${cleanRelative}`;

        // 이중 슬래시 제거
        return combined.replace(/\/+/g, '/');
    }


    /**
     * 상대 경로를 완전한 절대 URL로 변환 (파일 시스템 전용)
     * @param {string} path - 변환할 경로
     * @param {string} basePath - 기본 경로 (옵션)
     * @returns {string} 완전한 절대 URL (예: 'http://localhost:3000/examples/about')
     */
    resolvePath(path, basePath = null) {
        // basePath가 제공되지 않으면 config에서 가져오기
        if (basePath === null) {
            basePath = this.config?.basePath || '/';
        }
        const currentOrigin = window.location.origin;
        
        // 이미 완전한 HTTP URL인 경우 변환 불필요
        if (path.startsWith('http')) {
            return path;
        }

        // 절대 경로 → 완전한 URL 변환
        if (path.startsWith('/')) {
            const combinedPath = this.combinePaths(basePath, path);
            return `${currentOrigin}${combinedPath}`;
        }
        
        // 상대 경로인 경우 현재 경로 기준으로 해결
        const currentPathname = window.location.pathname;
        const currentBase = currentPathname.endsWith('/') 
            ? currentPathname 
            : currentPathname.substring(0, currentPathname.lastIndexOf('/') + 1);
        
        // 상대 경로 정규화
        const resolvedPath = this.combinePaths(currentBase, path);
        
        return `${currentOrigin}${resolvedPath}`;
    }



    /**
     * 로깅 래퍼 메서드
     */
    log(level, ...args) {
        if (this.errorHandler) {
            this.errorHandler.log(level, 'Router', ...args);
        }
    }

    /**
     * 통합 초기화 - 매니저 생성 → 비동기 로딩 → 라우터 시작
     */
    async initialize() {
        try {
            // 1. 매니저 초기화 (동기)
            // 항상 필요한 매니저들
            this.cacheManager = new CacheManager(this, this.config);
            this.stateHandler = new StateHandler(this);
            this.routeLoader = new RouteLoader(this, this.config);
            this.queryManager = new QueryManager(this);
            this.errorHandler = new ErrorHandler(this, this.config);
            
            // 조건부 매니저들
            if (this.config.useI18n) {
                try {
                    this.i18nManager = new I18nManager(this, this.config);
                    if (this.i18nManager.initPromise) {
                        await this.i18nManager.initPromise;
                    }
                    this.log('info', 'I18nManager initialized successfully');
                } catch (i18nError) {
                    this.log('warn', 'I18nManager initialization failed, continuing without i18n:', i18nError.message);
                    this.i18nManager = null; // i18n 매니저 비활성화
                    this.config.useI18n = false; // i18n 비활성화
                }
            }
            
            if (this.config.authEnabled) {
                this.authManager = new AuthManager(this, this.config);
            }

            // Create progress bar element
            this._createProgressBar();

            
            // 2. 라우터 시작
            this.isReady = true;
            this.init();
            
        } catch (error) {
            this.log('error', 'Router initialization failed:', error);
            // 실패해도 라우터는 시작 (graceful degradation)
            this.isReady = true;
            this.init();
        }
    }

    /**
     * 라우터가 준비될 때까지 대기
     */
    async waitForReady() {
        if (this.isReady) return true;
        if (this.readyPromise) {
            await this.readyPromise;
        }
        return this.isReady;
    }


    init() {
        const isHashMode = this.config.mode === 'hash';
        
        // 이벤트 리스너 등록 (메모리 최적화)
        window.addEventListener(
            isHashMode ? 'hashchange' : 'popstate',
            this._boundHandleRouteChange
        );
        
        // DOM 로드 처리 통합
        const initRoute = () => {
            if (isHashMode && !window.location.hash) {
                window.location.hash = '#/';
            } else {
                this.handleRouteChange();
            }
        };
        
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initRoute);
        } else {
            // requestAnimationFrame으로 성능 개선
            requestAnimationFrame(initRoute);
        }
    }

    handleRouteChange() {
        const { route, queryParams } = this._parseCurrentLocation();

        // 변경사항이 있을 때만 로드 (비교 후 저장해야 정확한 비교 가능)
        const paramsChanged = this.queryManager?.hasQueryParamsChanged(queryParams);
        this.queryManager?.setCurrentQueryParams(queryParams);

        if (route !== this.currentHash || paramsChanged) {
            this.currentHash = route;
            this.loadRoute(route);
        }
    }

    /**
     * 현재 위치 파싱 (분리하여 가독성 향상)
     */
    _parseCurrentLocation() {
        if (this.config.mode === 'hash') {
            const hashPath = window.location.hash.slice(1) || '/';
            const [pathPart, queryPart] = hashPath.split('?');

            // 경로 파싱 최적화
            let route = this.config.defaultRoute;
            if (pathPart && pathPart !== '/') {
                route = pathPart.startsWith('/') ? pathPart.slice(1) : pathPart;
            }

            return {
                route: route || this.config.defaultRoute,
                queryParams: this.queryManager?.parseQueryString(queryPart || window.location.search.slice(1)) || {}
            };
        } else {
            // History Mode - 서브폴더 배포 지원
            const fullPath = window.location.pathname;
            const basePath = this.config.basePath || '/';
            
            // base path 제거하여 실제 route 추출
            let route = fullPath;
            if (basePath !== '/' && fullPath.startsWith(basePath)) {
                route = fullPath.slice(basePath.length);
            }
            
            // 맨 앞의 / 제거
            if (route.startsWith('/')) {
                route = route.slice(1);
            }

            return {
                route: route || this.config.defaultRoute,
                queryParams: this.queryManager?.parseQueryString(window.location.search.slice(1)) || {}
            };
        }
    }

    async loadRoute(routeName) {
        // 전환이 진행 중이면 무시
        const inProgress = this.transitionInProgress;
        
        if (inProgress) {
            return;
        }

        try {
            this.transitionInProgress = true;

            // Show progress bar (with 0.3s delay)
            this._showProgressBar();

            // 인증 체크
            const authResult = this.authManager ?
                await this.authManager.checkAuthentication(routeName) :
                { allowed: true, reason: 'auth_disabled' };
            if (!authResult.allowed) {
                // Hide progress bar
                this._hideProgressBar();

                // 인증 실패 시 로그인 페이지로 리다이렉트
                if (this.authManager) {
                    this.authManager.emitAuthEvent('auth_required', {
                        originalRoute: routeName,
                        loginRoute: this.config.loginRoute
                    });

                    // navigateTo를 사용하여 이전 페이지로 돌아가기 지원
                    if (routeName !== this.config.loginRoute) {
                        this.navigateTo(this.config.loginRoute, { redirect: routeName });
                    } else {
                        this.navigateTo(this.config.loginRoute);
                    }
                }
                return;
            }

            const appElement = document.getElementById('app');
            if (!appElement) {
                throw new Error('App element not found');
            }

            // Vue 컴포넌트 생성 (백그라운드에서)
            const component = await this.routeLoader.createVueComponent(routeName);

            // 새로운 페이지를 오버레이로 렌더링
            await this.renderComponentWithTransition(component, routeName);

            // Hide progress bar
            this._hideProgressBar();

        } catch (error) {
            // Hide progress bar on error
            this._hideProgressBar();
            this.log('error', `Route loading failed [${routeName}]:`, error.message);
            
            
            // 에러 타입에 따른 처리
            if (this.errorHandler) {
                await this.errorHandler.handleRouteError(routeName, error);
            } else {
                // ErrorHandler가 없는 극단적 상황 - 최소한의 에러 표시
                console.error('[Router] Critical: No error handler available for route error:', error);
            }
        } finally {
            // 모든 처리가 완료된 후 전환 상태 리셋
            this.transitionInProgress = false;
        }
    }

    async renderComponentWithTransition(vueComponent, routeName) {
        const appElement = document.getElementById('app');
        if (!appElement) return;

        // 새로운 페이지 컨테이너 생성 (숨김 상태로 마운트)
        const newPageContainer = document.createElement('div');
        newPageContainer.className = 'page-container page-pending';
        newPageContainer.id = `page-${routeName.replace(/\//g, '-')}-${Date.now()}`;
        newPageContainer.style.cssText = 'position:absolute;top:0;left:0;width:100%;opacity:0;pointer-events:none;';

        // 새 컨테이너를 앱에 추가 (이전 페이지 위에 숨겨진 상태)
        appElement.appendChild(newPageContainer);

        // mounted() 완료 시그널용 Promise
        let resolveMounted;
        const mountedReady = new Promise(r => { resolveMounted = r; });

        // 새로운 Vue 앱을 새 컨테이너에 마운트 (숨김 상태에서 mounted 실행 → API 호출)
        const { createApp } = Vue;
        const newVueApp = createApp(vueComponent);

        // mounted() 완료 시 resolve 함수 주입
        newVueApp.config.globalProperties._resolveMounted = resolveMounted;

        // Vue 3 전역 속성 설정
        newVueApp.config.globalProperties.$router = {
            navigateTo: (route, params) => this.navigateTo(route, params),
            getCurrentRoute: () => this.getCurrentRoute(),

            // 파라미터 관리
            getParams: () => this.queryManager?.getAllParams() || {},
            getParam: (key, defaultValue) => this.queryManager?.getParam(key, defaultValue),

            // 유틸리티 메서드
            resolvePath: (path) => this.resolvePath(path),

            // 컴포넌트 생성
            createComponent: async (componentName) => {
                try {
                    return await this.routeLoader.createVueComponent(componentName);
                } catch (error) {
                    if (this.errorHandler) this.errorHandler.warn('Router', `Failed to create component '${componentName}':`, error);
                    throw error;
                }
            },

            // 캐시 관리
            clearCache: () => this.cacheManager?.clearAll() || 0
        };

        // Vue 앱을 DOM에 마운트 (숨김 상태 — mounted()에서 API 호출 시작)
        newVueApp.mount(`#${newPageContainer.id}`);

        // mounted() 완료 대기 (타임아웃: 기본 5초)
        const settleTimeout = this.config.dataSettleTimeout || 5000;
        await Promise.race([
            mountedReady,
            new Promise(r => setTimeout(r, settleTimeout))
        ]);

        // === 데이터 준비 완료 — 이제 페이지 전환 시작 ===

        // 기존 컨테이너에 exiting 클래스 추가
        const existingContainers = appElement.querySelectorAll('.page-container:not(.page-pending)');
        existingContainers.forEach(container => {
            container.classList.remove('page-entered');
            container.classList.add('page-exiting');
        });

        // 새 페이지 표시 (숨김 해제)
        newPageContainer.classList.remove('page-pending');
        newPageContainer.classList.add('page-entered');
        newPageContainer.style.cssText = '';

        // 라우트 이동 시 스크롤을 맨 위로 이동
        window.scrollTo(0, 0);

        // requestAnimationFrame으로 이전 페이지 정리
        requestAnimationFrame(() => {
            this.cleanupPreviousPages();
        });

        // 이전 앱 정리 준비
        if (this.currentVueApp) {
            this.previousVueApp = this.currentVueApp;
        }

        this.currentVueApp = newVueApp;
    }

    cleanupPreviousPages() {
        const appElement = document.getElementById('app');
        if (!appElement) return;

        // 배치 DOM 조작으로 성능 개선
        const fragment = document.createDocumentFragment();
        const exitingContainers = appElement.querySelectorAll('.page-container.page-exiting');
        
        // 한번에 제거
        exitingContainers.forEach(container => container.remove());

        // 이전 Vue 앱 정리
        if (this.previousVueApp) {
            try {
                this.previousVueApp.unmount();
            } catch (error) {
                // 무시 (이미 언마운트된 경우)
            }
            this.previousVueApp = null;
        }

        // 로딩 엘리먼트 제거
 
            appElement.querySelector('.loading')?.remove();
    }

    /**
     * Create progress bar element
     */
    _createProgressBar() {
        const progressBar = document.createElement('div');
        progressBar.id = 'viewlogic-progress-bar';
        progressBar.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 0%;
            height: 2px;
            background-color: rgba(59, 130, 246, 0.6);
            transition: width 0.3s ease-out, opacity 0.3s ease-out;
            z-index: 9999;
            opacity: 0;
        `;
        document.body.appendChild(progressBar);
        this.progressBarElement = progressBar;
    }

    /**
     * Show progress bar with 0.3s delay
     */
    _showProgressBar() {
        // Clear any existing timer
        if (this.progressBarTimer) {
            clearTimeout(this.progressBarTimer);
        }

        // Set timer for 0.3s delay
        this.progressBarTimer = setTimeout(() => {
            if (this.progressBarElement) {
                this.progressBarElement.style.opacity = '1';
                this.progressBarElement.style.width = '70%';
            }
        }, 300); // 0.3 seconds
    }

    /**
     * Hide progress bar
     */
    _hideProgressBar() {
        // Clear timer if loading finished before 0.3s
        if (this.progressBarTimer) {
            clearTimeout(this.progressBarTimer);
            this.progressBarTimer = null;
        }

        // Hide progress bar
        if (this.progressBarElement) {
            this.progressBarElement.style.width = '100%';
            setTimeout(() => {
                if (this.progressBarElement) {
                    this.progressBarElement.style.opacity = '0';
                    this.progressBarElement.style.width = '0%';
                }
            }, 200);
        }
    }

    navigateTo(routeName, params = null) {
        // If routeName is an object, treat it as {route, params}
        if (typeof routeName === 'object') {
            params = routeName.params || null;
            routeName = routeName.route;
        }

        // Normalize route name: remove leading slash if present
        // This allows both navigateTo('about') and navigateTo('/about')
        if (typeof routeName === 'string' && routeName.startsWith('/')) {
            routeName = routeName.substring(1);
        }

        // Handle empty route or root
        if (!routeName || routeName === '') {
            routeName = this.config.defaultRoute;
        }

        // Clear current query params if navigating to a different route
        if (routeName !== this.currentHash && this.queryManager) {
            this.queryManager.clearQueryParams();
        }

        // Set route parameters
        if (this.queryManager) {
            this.queryManager.setCurrentRouteParams(params);
        }

        // Update URL with new route and params
        this.updateURL(routeName, params);
    }

    getCurrentRoute() {
        return this.currentHash ? `/${this.currentHash}` : '/';
    }


    updateURL(route, params = null) {
        const queryParams = params || this.queryManager?.getQueryParams() || {};
        const queryString = this.queryManager?.buildQueryString(queryParams) || '';

        // URL 생성
        let base = route === this.config.defaultRoute ? '/' : `/${route}`;

        if (this.config.mode === 'hash') {
            // 해시 모드: basePath 제외
            const url = queryString ? `${base}?${queryString}` : base;
            const newHash = `#${url}`;

            // 동일한 URL이면 업데이트하지 않음 (성능 최적화)
            if (window.location.hash !== newHash) {
                window.location.hash = newHash;
            }
        } else {
            // 히스토리 모드: basePath 포함
            base = this.combinePaths(this.config.basePath, base);
            const url = queryString ? `${base}?${queryString}` : base;

            // 모든 라우트 변경에 대해 뒤로가기 지원
            window.history.pushState({}, '', url);
            this.handleRouteChange();
        }
    }

    /**
     * 라우터 정리 (메모리 누수 방지)
     */
    destroy() {
        // 이벤트 리스너 제거
        window.removeEventListener(
            this.config.mode === 'hash' ? 'hashchange' : 'popstate',
            this._boundHandleRouteChange
        );
        
        // 현재 Vue 앱 언마운트
        if (this.currentVueApp) {
            this.currentVueApp.unmount();
            this.currentVueApp = null;
        }
        
        // 이전 Vue 앱 언마운트
        if (this.previousVueApp) {
            this.previousVueApp.unmount();
            this.previousVueApp = null;
        }
        
        // 매니저 정리
        Object.values(this).forEach(manager => {
            if (manager && typeof manager.destroy === 'function') {
                manager.destroy();
            }
        });
        
        // 캐시 클리어
        this.cacheManager?.clearAll();
        
        // DOM 정리
        const appElement = document.getElementById('app');
        if (appElement) {
            appElement.innerHTML = '';
        }
        
        this.log('info', 'Router destroyed');
    }
}
// 전역 라우터는 index.html에서 환경설정과 함께 생성됨